import { telemetry } from '../../../@vendor/Posthog';
import { ObjectUpdate } from '../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Point } from '../../../platform/geom/Point';
import { KeyInput } from '../../../platform/input/KeyInput';
import { Keys } from '../../../platform/input/Keys';
import { Colors } from '../../../ui';
import { PointerInfo } from '../../canvas/PointerInfo';
import { Selection } from '../../canvas/Selection';
import { Phys } from '../../canvas/entities/Phys';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { CreateTransformableDragParams, MultiCreatePhysicalToolBehavior } from '../../canvas/tooling/MultiCreatePhysicalToolBehavior';
import { ToolSettings } from '../../canvas/tooling/ToolBehavior';
import { ConnectTarget } from './ConnectTarget';
import { Connector } from './Connector';
import { connectorConnect, connectorDisconnect } from './ConnectorCommands';
import { ConnectorCapStyle, ConnectorMode, ConnectorStrokeStyle } from './ConnectorEnums';

const defaultSettings: ToolSettings = {
    mode: ConnectorMode.smooth,
    strokeWidth: 3,
    strokeStyle: ConnectorStrokeStyle.solid,
    startCap: ConnectorCapStyle.none,
    endCap: ConnectorCapStyle.solidArrow,
    color: Colors.text,
};

class CreateConnectorToolBehavior2 extends MultiCreatePhysicalToolBehavior(Connector) {

    private forcedMode: 'drag' | 'drop' | 'plop' = 'drag';

    public init(settings: Readonly<Record<string, any>>, param?: string) {
        super.init(settings, param === 'plop' ? 'plop' : 'drag');
        if (param === 'plop') {
            this.forcedMode = 'plop';
            this.startPlop(this.session.get(PointerInfo).position);
            return 'creating';
        }
    }

    public onKeyDown(state: string, ki: KeyInput): string | void {
        const newState = super.onKeyDown(state, ki);
        if (!!newState && newState !== state) return newState;

        if (ki.key === Keys.KEY_1 || ki.key === Keys.KEY_2 || ki.key === Keys.KEY_3) {
            ki.squash();
            this.settings = {
                ...this.settings,
                strokeStyle:
                    (ki.key === Keys.KEY_2 ? ConnectorStrokeStyle.dashed : null) ??
                    (ki.key === Keys.KEY_3 ? ConnectorStrokeStyle.dotted : null) ??
                    ConnectorStrokeStyle.solid,
            };
            this.syncSubjects();
        }

        if (ki.key === Keys.TAB) {
            ki.squash();
            this.settings = {
                ...this.settings,
                startCap: this.settings.endCap ?? ConnectorCapStyle.none,
                endCap: this.settings.startCap ?? ConnectorCapStyle.none,
            };
            this.syncSubjects();
        }

        if (state === 'idle' && ki.key === Keys.KEY_X) {
            this.forcedMode = 'plop';
            this.startPlop(this.session.get(PointerInfo).position);
            return 'creating';
        }
    }

    // public onWheel(state: string, e: WheelInput): string | void {
    //     if (state === 'creating' && e.modifiers.shift) {
    //         const sw = this.settings.strokeWidth || 3;
    //         this.settings = {
    //             ...this.settings,
    //             strokeWidth: Math.max(1, sw + e.delta < 0 ? -1 : 1),
    //         };
    //         this.syncSubjects();
    //     }
    // }

    protected get mode() {
        return this.forcedMode;
    }

    protected shouldMultiClickDrag(): boolean {
        return true;
    }

    protected createSubjects(u: ObjectUpdate): Connector[] {

        if (this.mode === 'drag') {
            telemetry.emit('interaction.connectorCreate');
        }

        const targets = this.session.get(Selection).polarize(ConnectTarget);
        const subjects = this.mode === 'plop'
            ? targets.map(() => u.create(Connector))
            : [u.create(Connector)];

        for (let i = 0; i < subjects.length; i++) {
            const s = subjects[i];
            this.resolveRoot().add(s);
            const t = targets[i];
            if (this.mode === 'plop' && t) {
                s.fromId = t.id;
                s.fromOffset = new Point(0.5, 0.5);
                s.applyOffsets();
            }
        }

        this.session.apply(Selection, x => x.replace(subjects));
        return subjects;
    }

    protected updateSubjects(subjects: Connector[]): void {
        ToolSettings.setStylePropertiesViaSettings(subjects, { ...defaultSettings, ...this.settings });
    }

    protected applyDrag(subjects: Connector[], drag: CreateTransformableDragParams): void {
        const subject = subjects[0];

        subject.nodes = [
            drag.from,
            drag.to,
        ];

        if (!subject.fromId) {
            const fromHit = Phys.from(this.spatial.lookup(drag.from, ConnectTarget)).filter(x => !x.bounded()).highest();
            if (fromHit) {
                this.dispatch(connectorConnect, [subject.id, 'start', fromHit.id, fromHit.toRelative(drag.from)]);
            }
        }

        const toHit = Phys.from(this.spatial.lookup(drag.to, ConnectTarget)).filter(x => !x.bounded()).highest();
        if (toHit) {
            this.dispatch(connectorConnect, [subject.id, 'end', toHit.id, toHit.toRelative(drag.to)]);
        }
    }

    protected applyPlop(subjects: Connector[], vpt: Point): void {
        const mpt = this.camera.toModel(vpt);
        for (const s of subjects) {
            const sf = s.createFigure();
            s.setFromFigure([...sf.slice(0, -1), mpt]);

            const toHit = Phys.from(this.spatial.lookup(mpt, ConnectTarget)).filter(x => !x.bounded()).highest();
            if (toHit && toHit.id !== s.fromId) {
                this.dispatch(connectorConnect, [s.id, 'end', toHit.id, toHit.toRelative(mpt)]);
            }
            else {
                this.dispatch(connectorDisconnect, [s.id, 'end']);
            }
        }
    }

    protected paintSubjects(subject: Connector[], gfx: UniversalSurfaceRenderer) {

        // const from = this.objects.getMaybe(subject.fromId, ConnectTarget);
        // const to = this.objects.getMaybe(subject.toId, ConnectTarget);
        // gfx.setTransform(this.camera.modelViewMatrix);

        // if (from) {
        //     gfx.lineWidth = 2;
        //     gfx.strokeStyle = Colors.primaryLight;
        //     gfx.strokeRect(...from.bounds(RelativeTo.Model).toTuple());
        // }
        // if (to) {
        //     gfx.lineWidth = 2;
        //     gfx.strokeStyle = Colors.primaryLight;
        //     gfx.strokeRect(...to.bounds(RelativeTo.Model).toTuple());
        // }
    };

    protected finalizeSubjects(subjects: Connector[]): void {
        for (const s of subjects) {
            s.applyOffsets();
        }
    }
}

export const connectorTool = defineToolWithCommonBehaviors({
    id: 'connectorTool',
    label: 'Connectors',
    category: 'collaboration',
    icon: 'deqo://icons/d2/connector',
    weight: 1046,
    shortcut: 'A',
    behaviors: [
        CreateConnectorToolBehavior2,
    ]
});

